import React, { forwardRef, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Link2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, SeverityBadge } from '@/components/primitives';
import VulnBrief, { PriorityBadge } from '@/pages/dashboard/VulnBrief';
import { copyText } from '@/pages/dashboard/api';

/**
 * The CVE brief, docked at the right of the list (or as a drawer on narrower screens, full screen on a
 * phone), the same way the Threats page shows a threat. The header comes from the list row, so it is
 * there instantly; the brief loads under it.
 */
const VulnPanel = forwardRef(function VulnPanel({ row, cveId, tab, onTab, onClose, onStep, canPrev, canNext, mode }, headingRef) {
  const [huntCount, setHuntCount] = useState(null);
  useEffect(() => { setHuntCount(null); }, [cveId]);

  const copyLink = async () => {
    if (await copyText(window.location.href)) toast.success('Link copied');
    else toast.error("Couldn't copy the link");
  };
  const product = row ? [row.vendor, row.product].filter(Boolean).join(' ') : '';

  return (
    <aside className={`vp-panel vp-panel--${mode}`} aria-label={`${cveId} brief`} data-testid="vuln-panel">
      <header className="vp-panel-head">
        <div className="vp-panel-top">
          <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={() => onStep(-1)} disabled={!canPrev} aria-label="Previous CVE" title="Previous CVE (K)"><ChevronUp aria-hidden="true" /></button>
          <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={() => onStep(1)} disabled={!canNext} aria-label="Next CVE" title="Next CVE (J)"><ChevronDown aria-hidden="true" /></button>
          <span className="vp-spacer" />
          <a className="is-btn is-btn--ghost is-btn--sm" href={`https://nvd.nist.gov/vuln/detail/${cveId}`} target="_blank" rel="noopener noreferrer">NVD <ExternalLink aria-hidden="true" /></a>
          <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={copyLink} aria-label="Copy link to this CVE"><Link2 aria-hidden="true" /></button>
          <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={onClose} aria-label="Close panel (Esc)"><X aria-hidden="true" /></button>
        </div>
        <h2 className="vp-panel-title dash-mono" ref={headingRef} tabIndex={-1}>{cveId}</h2>
        {(row?.title || product) && <p className="vp-panel-sub">{row.title || product}</p>}
        {row && (
          <div className="vp-panel-badges">
            <PriorityBadge decision={row.priority} size="md" />
            {row.cvss_score != null && <span className="vp-cvss"><span className="dash-mono">{Number(row.cvss_score).toFixed(1)}</span><SeverityBadge severity={row.cvss_severity} size="sm" /></span>}
            {row.kev && <Badge tone="warning" size="sm">CISA KEV</Badge>}
            {row.kev_ransomware_use === 'Known' && <Badge tone="danger" size="sm">Ransomware</Badge>}
            {row.in_stack && <Badge tone="info" size="sm">In your stack</Badge>}
            <button type="button" className="vp-huntjump" onClick={() => onTab('hunting')} aria-label="Jump to threat hunting queries">
              Threat hunting{huntCount == null ? '' : huntCount ? `: ${huntCount} ${huntCount === 1 ? 'query' : 'queries'}` : ': none'}
            </button>
          </div>
        )}
      </header>
      <div className="vp-panel-body">
        <VulnBrief key={cveId} cveId={cveId} tab={tab || 'overview'} onTab={onTab} onHuntCount={setHuntCount} />
      </div>
    </aside>
  );
});

export default VulnPanel;
